import { CertifiedMetricsParams } from "./table.types";
import { DailyMetricsByDateByRobotResponse } from "protos/portal/metrics";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { Metric } from "portal/utils/metrics";
import { portalApi } from "portal/state/portalApi";
import { ReportMode } from "protos/portal/reports";
import { RobotResponse, RobotSummaryResponse } from "protos/portal/robots";
import { toCertifiedFields } from "./helpers";
import { useEffect, useMemo, useState } from "react";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";

/**
 * Loads certified daily metrics for the report table when mode is "days" and
 * robots, date range, summaries, and visible metrics are all available.
 */
export const useReportTableDailyMetrics = ({
  mode,
  selectedRobots,
  dateRange,
  summaries,
  useJigsaw,
  visibleMetrics,
}: {
  mode: ReportMode;
  selectedRobots: RobotResponse[];
  dateRange: DateRange<DateTime>;
  summaries: RobotSummaryResponse[] | undefined;
  useJigsaw: boolean;
  visibleMetrics: Metric[];
}): {
  dayMetrics?: DailyMetricsByDateByRobotResponse;
  dayMetricsError: boolean;
  fetchDailyMetricsBody?: CertifiedMetricsParams;
  isDayMetricsLoading: boolean;
} => {
  const { i18n } = useTranslation();

  const [rangeStart, rangeEnd] = dateRange;
  const [getCertifiedDailyMetrics] = useMutationPopups(
    portalApi.endpoints.getDailyCertifiedMetrics.useMutation()
  );
  const [dayMetrics, setDayMetrics] =
    useState<DailyMetricsByDateByRobotResponse>();
  const [isDayMetricsLoading, setIsDayMetricsLoading] = useState(false);
  const [dayMetricsError, setDayMetricsError] = useState(false);

  const fetchDailyMetricsBody = useMemo(():
    | CertifiedMetricsParams
    | undefined => {
    if (
      mode === ReportMode.REPORT_MODE_DAYS &&
      selectedRobots.length > 0 &&
      rangeStart &&
      rangeEnd &&
      summaries &&
      visibleMetrics.length > 0
    ) {
      return {
        serials: selectedRobots.map((robot) => robot.serial),
        startDate: rangeStart.toFormat(DATE_PATH_FORMAT),
        endDate: rangeEnd.toFormat(DATE_PATH_FORMAT),
        fields: toCertifiedFields(visibleMetrics),
        useJigsaw,
      };
    }
  }, [
    mode,
    rangeEnd,
    rangeStart,
    selectedRobots,
    summaries,
    useJigsaw,
    visibleMetrics,
  ]);

  useEffect(() => {
    if (!fetchDailyMetricsBody) {
      setDayMetrics(undefined);
      setDayMetricsError(false);
      setIsDayMetricsLoading(false);
      return;
    }
    const fetchDailyMetrics = async (body: {
      serials: string[];
      startDate: string;
      endDate: string;
      fields: string[];
      useJigsaw: boolean;
    }): Promise<void> => {
      setIsDayMetricsLoading(true);
      const { data, error } = await getCertifiedDailyMetrics({
        ...body,
        language: i18n.language,
      });
      if (error) {
        setDayMetricsError(true);
      }
      if (data) {
        setDayMetrics(data);
      }
      setIsDayMetricsLoading(false);
    };

    fetchDailyMetrics(fetchDailyMetricsBody);
  }, [getCertifiedDailyMetrics, fetchDailyMetricsBody, i18n.language]);

  return {
    dayMetrics,
    dayMetricsError,
    fetchDailyMetricsBody,
    isDayMetricsLoading,
  };
};
